/** @format */

import { ChangeDetectorRef, Component, DestroyRef, OnInit, input, inject } from '@angular/core';
import {
	FulfillmentButtonInterface,
	selectInStorePickupButtonText,
	selectShipMode,
	setFulfillmentButtonsShipMode
} from '@pdp/add-to-cart/data-access';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	selectAllProductSkusPartNumber,
	selectBundleInStorePickupButtonText,
	selectIsBOPISLOnly,
	selectResolvedSkuPartNumber
} from '@pdp/data-access/state';

import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'pdp-feature-in-store-pickup-button',
	templateUrl: './in-store-pickup-button.component.html',
	styleUrl: './in-store-pickup-button.component.scss',
	standalone: false
})
export class InStorePickupButtonComponent implements OnInit {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly store = inject(Store);
	private readonly destroyRef = inject(DestroyRef);

	readonly isBundle = input(false);
	readonly pickupInOneHourTextEnabled = input(false);
	readonly nearbyStoresButtonEnabled = input(false);

	inStorePickupButtonText$: Observable<FulfillmentButtonInterface>;
	shipMode$ = this.store.select(selectShipMode);
	resolvedSkuId$ = this.store.select(selectResolvedSkuPartNumber);
	productSkusPartNumber$ = this.store.select(selectAllProductSkusPartNumber);
	isBOPISLOnly$ = this.store.select(selectIsBOPISLOnly);

	fulfillmentIconName = 'fulfillment-pickup-outlined';

	ngOnInit(): void {
		this.inStorePickupButtonText$ = this.getInStorePickupButtonTextSelector();
		this.addFeatureFlagToButtonTextObservable(this.inStorePickupButtonText$);
		combineLatest([this.inStorePickupButtonText$, this.shipMode$, this.isBOPISLOnly$])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([inStorePickupButtonText, shipMode, isBOPISLOnly]) => {
				if (isBOPISLOnly) {
					this.setShipMode(inStorePickupButtonText.isBopl);
				} else if (shipMode === 'bopis' && inStorePickupButtonText.isBopl) {
					this.setShipMode(true);
				} else if (shipMode === 'bopl' && !inStorePickupButtonText.isBopl) {
					this.setShipMode(false);
				}

				const buttonEnabled = !inStorePickupButtonText.fulfillmentButtonDisabledClass;
				this.getFulfillmentIconName(shipMode, buttonEnabled);
				this.cdr.detectChanges();
			});
	}

	addFeatureFlagToButtonTextObservable(buttonText$: Observable<FulfillmentButtonInterface>) {
		this.inStorePickupButtonText$ = buttonText$.pipe(
			map((buttonText: FulfillmentButtonInterface) => {
				if (
					!this.pickupInOneHourTextEnabled() &&
					buttonText.fulfillmentButtonText?.includes('Ready')
				) {
					return {
						...buttonText,
						fulfillmentButtonText: buttonText.nonDynamicFullfillmentButtonText
					};
				}
				if (
					this.nearbyStoresButtonEnabled() &&
					buttonText.changeStoreButtonText?.includes('See')
				) {
					return {
						...buttonText,
						changeStoreButtonText: 'Available Nearby'
					};
				}
				return buttonText;
			})
		);
	}

	setShipMode(isBopl: boolean) {
		if (isBopl) {
			this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'bopl' }));
		} else {
			this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'bopis' }));
		}
	}

	getFulfillmentIconName(shipMode: string, buttonEnabled: boolean): void {
		const isBopisOrBopl = shipMode === 'bopis' || shipMode === 'bopl';
		if (isBopisOrBopl && buttonEnabled) {
			this.fulfillmentIconName = 'fulfillment-pickup-filled';
		} else {
			this.fulfillmentIconName = 'fulfillment-pickup-outlined';
		}
	}

	getInStorePickupButtonTextSelector(): Observable<FulfillmentButtonInterface> {
		return this.isBundle()
			? this.store.select(selectBundleInStorePickupButtonText)
			: this.store.select(selectInStorePickupButtonText);
	}
}
